import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { ReduxState } from "../../../redux";
import { useTour } from "./TourContext";
import {
  SUB_TOURS_MENU,
  SUB_TOUR_ANALYZE,
  analyzeTourIdForSegment
} from "./tourDefinitions";

type Props = {
  /**
   * Visual style.
   * - "header": subtle icon+label button for the app header
   * - "inline": primary-colored button suitable for empty states
   */
  variant?: "header" | "inline";
  label?: string;
};

// Tours target seeded sandbox data (specific jobs, templates, rows), so
// they only render in sandbox mode. Live workspaces either lack the data
// or have unpredictable user data that breaks step selectors.
const TourMenuButton: React.FC<Props> = ({ variant = "header", label }) => {
  const [open, setOpen] = useState(false);
  const { startTour, state } = useTour();
  const isSandboxMode = useSelector(
    (s: ReduxState) => s.Auth.isSandboxMode
  );
  const segment = useSelector((s: ReduxState) => {
    const tk: any = (s as any).Auth?.token;
    return tk?.sandboxSegment || tk?.segment || null;
  });

  if (!isSandboxMode) return null;

  const handleStart = (tourId: string) => {
    setOpen(false);
    // The generic "sub-analyze" entry in the menu should launch whichever
    // segment-tailored variant matches the current user/sandbox.
    const effectiveId =
      tourId === SUB_TOUR_ANALYZE.id ? analyzeTourIdForSegment(segment) : tourId;
    startTour(effectiveId);
  };

  const buttonText = label || "Take a tour";

  let button: React.ReactNode;
  if (variant === "header") {
    button = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={state.active}
        title={state.active ? "A tour is already running" : buttonText}
        style={{
          background: "transparent",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          padding: "0.25rem 0.7rem",
          fontSize: "0.85rem",
          color: "#495057",
          cursor: state.active ? "not-allowed" : "pointer",
          opacity: state.active ? 0.5 : 1,
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem"
        }}
      >
        <FontAwesomeIcon icon="play-circle" />
        {buttonText}
      </button>
    );
  } else {
    button = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={state.active}
        className="btn btn-outline-primary btn-sm"
        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
      >
        <FontAwesomeIcon icon="play-circle" />
        {buttonText}
      </button>
    );
  }

  return (
    <>
      {button}
      <Modal show={open} onHide={() => setOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Pick a tour
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontSize: "0.9rem",
              color: "#6c757d",
              marginBottom: "1rem"
            }}
          >
            Each tour walks through one product area using the seeded demo
            data in this sandbox.
          </p>
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {SUB_TOURS_MENU.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleStart(t.id)}
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "0.7rem 0.9rem",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.6rem"
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>{t.emoji}</span>
                <span>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                    {t.description}
                  </div>
                </span>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TourMenuButton;
